import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { usePermissions } from '../../../../hooks/usePermissions';
import { formatDateString } from '../../../../utils/dates/dateMethods';
import { getCurrency } from '../../../../utils/tools/currencyTools';
import { IEnrollment } from '../common/types';
import DetailedFields from './DetailedFields';
import InfoSection from './InfoSection';

const Container = styled.div`
    font-family: Arial, sans-serif;
    color: ${({ theme }) => theme.gray2};
`;

const DatesContainer = styled.div`
    display: flex;
    gap: 2rem;
    border-top: 1px solid #eee;
    padding-top: 0.5rem;
    flex-direction: column;

    @media (min-width: 769px) {
        flex-direction: row;
        gap: 0.7rem;
    }

    & > div {
        width: 50%;
        @media (max-width: 768px) {
            width: 100%;
        }
    }

    & > div:not(:first-child) {
        width: 100%;

        @media (min-width: 769px) {
            width: 65%;
        }
    }
`;

const StyledDates = styled.div`
    display: grid;
    grid-template-columns: 30% 1fr;
    align-items: start;
    gap: 0.7rem 3rem;

    @media (min-width: 769px) {
        grid-template-columns: auto 1fr;
    }
`;

const StyledRow = styled.div`
    display: flex;
    gap: 3rem;
    flex-direction: column;
    margin-bottom: 1rem;

    @media (min-width: 769px) {
        flex-direction: row;
    }

    & > div:first-child {
        width: 100%;
        @media (min-width: 769px) {
            width: 40%;
        }
    }

    & > div:not(:first-child) {
        width: 100%;

        @media (min-width: 769px) {
            width: 60%;
        }
    }
`;

interface IEnrollmentDetailsProps {
    enrollment: IEnrollment;
    dataTestId: string;
}

const EnrollmentDetails: React.FC<IEnrollmentDetailsProps> = ({ enrollment, dataTestId }) => {
    const loggedInUserRole = useSelector((state: any) => state.common.environment.user.role);

    const { isAgencyUser } = usePermissions();

    const agencyUser = useMemo(() => isAgencyUser(loggedInUserRole), [loggedInUserRole, isAgencyUser]);

    const renderDetailedFields = (
        fields: { label: string; value: any; dataTestId: string; showColon?: boolean; isVisible?: boolean }[],
    ) => {
        return fields.map(
            (field, index) =>
                field.isVisible !== false && (
                    <DetailedFields
                        key={index}
                        label={field.label}
                        value={field.value}
                        dataTestId={field.dataTestId}
                        showColon={field.showColon}
                    />
                ),
        );
    };

    const premiumHistoryFields = useMemo(
        () => [
            {
                label: 'member.enrollment_list.premiumhistory.premiumAmount',
                value: getCurrency(enrollment.grossPremiumAmt || 0),
                dataTestId: 'premium-amount',
            },
            {
                label: 'member.enrollment_list.premiumhistory.electedFederalAPTCAmount',
                value: getCurrency(enrollment.aptcAmount || 0),
                dataTestId: 'aptc-amount',
            },
            {
                label: 'indportal.portalhome.PremiumCredit',
                value: getCurrency(enrollment.stateSubsidyAmount || 0),
                dataTestId: 'premium-amount',
                isVisible: enrollment.isStateSubsidyEnabled && enrollment.stateSubsidyAmount !== undefined,
            },
            {
                label: 'indportal.portalhome.CsrAmount',
                value: getCurrency(enrollment.csrAmount || 0),
                dataTestId: 'csr-amount',
                isVisible: !enrollment.isEnhancedCsrEnabled && enrollment.csrAmount !== undefined,
            },
            {
                label: 'indportal.portalhome.EnhancedCsrAmount',
                value: getCurrency(enrollment.csrAmount || 0),
                dataTestId: 'enhanced-csr-amount',
                isVisible: enrollment.isEnhancedCsrEnabled && enrollment.csrAmount !== undefined,
            },
            {
                label: 'indportal.portalhome.CsrAmount',
                value: getCurrency(enrollment.permCsrAmt || 0),
                dataTestId: 'csr-amount',
                isVisible: enrollment.isEnhancedCsrEnabled && enrollment.permCsrAmt !== undefined,
            },
            {
                label: 'member.enrollment_list.premiumhistory.netPremium',
                value: getCurrency(enrollment.netPremiumAmt),
                dataTestId: 'net-premium',
            },
        ],
        [enrollment],
    );

    const enrollmentIDsFields = useMemo(
        () => [
            {
                label: 'member.enrollment_list.premiumhistory.exchangeAssignedPolicyID',
                value: enrollment.enrollmentId,
                dataTestId: 'exchange-policy-id',
            },
            {
                label: 'member.enrollment_list.premiumhistory.CMSPlanId',
                value: enrollment.CMSPlanID,
                dataTestId: 'cms-plan-id',
            },
            {
                label: 'member.enrollment_list.premiumhistory.transactionID',
                value: enrollment.transactionId,
                dataTestId: 'transaction-id',
            },
        ],
        [enrollment],
    );

    const datesFields = useMemo(
        () => [
            {
                label: 'member.enrollment_list.premiumhistory.submittedDate',
                value: formatDateString(enrollment.creationTimestamp) || '',
                dataTestId: 'submitted-date',
                showColon: true,
            },
            {
                label: 'member.enrollment_list.premiumhistory.lastUpdateDate',
                value: formatDateString(enrollment.lastUpdateDate) || '',
                dataTestId: 'last-update-date',
                showColon: true,
            },
        ],
        [enrollment],
    );

    const submittedByFields = useMemo(
        () => [
            {
                label: 'member.enrollment_list.premiumhistory.submittedBy',
                value: agencyUser
                    ? enrollment.submittedByRoleName
                    : `${enrollment.submittedByUserName} (${enrollment.submittedByRoleName})`,
                dataTestId: 'submitted-by',
                showColon: true,
            },
            {
                label: 'member.enrollment_list.premiumhistory.lastUpdateBy',
                value: agencyUser
                    ? enrollment.lastUpdatedRoleName
                    : `${enrollment.lastUpdatedUserName} (${enrollment.lastUpdatedRoleName})`,
                dataTestId: 'last-update-by',
                showColon: true,
            },
        ],
        [enrollment, agencyUser],
    );

    const getStatus = (status: string): string => status?.replace('_', ' ') || '';

    return (
        <Container dataTestId={dataTestId}>
            <div className="grid-row margin-bottom-2">
                <div className="mobile:grid-col-12 tablet:grid-col-4 text-center">
                    <img
                        src={`/hix/admin/issuer/company/profile/logo/view/${enrollment.issuerId}`}
                        alt={`${enrollment.carrierName} logo`}
                        className="enrollment-carrier-logo"
                    />
                    <p>
                        <small>{enrollment.carrierName.toUpperCase()}</small>
                    </p>
                </div>
                <div className="mobile:grid-col-12 tablet:grid-offset-2 tablet:grid-col-6 text-center">
                    <h4>{enrollment.planName.toUpperCase()}</h4>
                </div>
            </div>

            <StyledRow>
                <InfoSection>
                    {renderDetailedFields([
                        {
                            label: 'member.enrollment_list.premiumhistory.enrollmentStatus',
                            value: getStatus(enrollment.enrollmentStatus),
                            dataTestId: 'enrollment-status',
                            showColon: true,
                        },
                        ...(enrollment.enrollmentConfirmationDate
                            ? [
                                  {
                                      label: 'member.enrollment_list.premiumhistory.confirmationDate',
                                      value: formatDateString(enrollment.enrollmentConfirmationDate) || '',
                                      dataTestId: 'confirmationDate',
                                      showColon: true,
                                  },
                              ]
                            : []),
                    ])}
                </InfoSection>
                <InfoSection>
                    {renderDetailedFields([
                        {
                            label: 'member.enrollment_list.premiumhistory.effectiveDate',
                            value: `${formatDateString(
                                enrollment.enrollmentBenefitEffectiveStartDate,
                            )} - ${formatDateString(enrollment.enrollmentBenefitEffectiveEndDate)}`,
                            dataTestId: 'effective-date',
                            showColon: true,
                        },
                    ])}
                </InfoSection>
            </StyledRow>

            <StyledRow>
                <InfoSection title="member.enrollment_list.premiumhistory.monthlyPayment">
                    {renderDetailedFields(premiumHistoryFields)}
                </InfoSection>
                <InfoSection title="member.enrollment_list.premiumhistory.enrollmentIDs">
                    {renderDetailedFields(enrollmentIDsFields)}
                </InfoSection>
            </StyledRow>

            <DatesContainer>
                <StyledDates>{renderDetailedFields(datesFields)}</StyledDates>
                <StyledDates>{renderDetailedFields(submittedByFields)}</StyledDates>
            </DatesContainer>
        </Container>
    );
};

export default EnrollmentDetails;
